import { memo, useMemo, useState } from 'react';
import { Marker } from '@adamscybot/react-leaflet-component-marker';
import { Tooltip } from '@ds';
import { MARKER_TYPE_ICONS } from '@lp/features/RoutePlanner/config';
import { AircraftMarkerType, MapFilterType } from '@lp/features/RoutePlanner/types';
import { AircraftAvailabilityView } from '@/api/apis/resources';
import { getFormattedTimeRange, getUnosId } from '@/utils/helpers';

const renderAircraftItem = (
  aircraft: Pick<
    AircraftAvailabilityView,
    'id' | 'availabilities' | 'reservations' | 'registration' | 'latitude' | 'longitude'
  >,
  timezone: string,
) => {
  const { id, registration, reservations, availabilities } = aircraft;
  const reservationDonorId = reservations[0]?.donorId;
  const organType = reservations[0]?.organType;
  const currentAvailability = availabilities[0];

  return (
    <div key={`${id}-${registration}`} className="flex gap-2 items-center">
      {MARKER_TYPE_ICONS[MapFilterType.aircraft]}
      <span className="text-s-sb text-black">{registration}</span>
      {!!currentAvailability && (
        <div className="flex gap-2 items-center">
          {reservationDonorId ? (
            <span className="flex bg-base-70 px-1 py-0.5 rounded-full text-s-r text-base-5">reserved</span>
          ) : (
            <span className="flex bg-success-80 px-1 py-0.5 rounded-full text-s-r text-base-5">on duty</span>
          )}
          <span className="text-s-sb text-black">
            {getFormattedTimeRange(currentAvailability.startUtc, currentAvailability.endUtc, timezone)}
          </span>
          {!!reservationDonorId && !!organType && (
            <span className="text-s-sb text-black">{`(${getUnosId(reservationDonorId, organType)})`}</span>
          )}
        </div>
      )}
    </div>
  );
};

export const AircraftMarker = memo(({ name, location, list, timezone }: AircraftMarkerType) => {
  const [isOpen, setIsOpen] = useState(false);

  const reservedListNumber = useMemo(() => {
    return list.filter((i) => i.reservations.length).length;
  }, [list]);

  const icon = useMemo(
    () => (
      <Tooltip
        placement="bottom"
        onOpenChange={(value) => setIsOpen(value)}
        showArrow
        closeDelay={0}
        classNames={{
          base: 'group',
          content: 'shadow-xl opacity-90',
        }}
        content={
          <div className="px-1 py-2 flex flex-col gap-2">
            <div className="text-m-sb text-black">{name}</div>
            {list.map((item) => renderAircraftItem(item, timezone))}
          </div>
        }
      >
        <div
          className={`w-max rounded-3xl py-1.5 px-2 flex gap-2 items-center text-base-5 shadow-xl hover:bg-base-60 ${isOpen ? 'bg-base-60' : 'bg-white'}`}
        >
          {MARKER_TYPE_ICONS[MapFilterType.aircraft]}
          <span className="text-s-sb text-base-5">{list.length - reservedListNumber}</span>
          {!!reservedListNumber && (
            <span className="block bg-base-70 px-1 text-s-sb text-base-30 rounded-md">{reservedListNumber}</span>
          )}
        </div>
      </Tooltip>
    ),
    [name, list, timezone, isOpen, reservedListNumber],
  );

  return (
    <Marker
      position={location}
      zIndexOffset={20}
      componentIconOpts={{
        disableClickPropagation: true,
      }}
      icon={icon}
    />
  );
});
